import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'

import ErrorHandler from '../services/ErrorHandler'
import OfficeService from '../services/OfficeService'
import { sessionState } from '../state'
import Database from '../types/Database'
import {
  OfficeFormData,
  UseOfficeFormReturn,
  UseOfficeFormOptions
} from '../types/ServiceTypes'

/**
 * Custom hook for managing office form state and operations
 * Handles form validation, submission, and integration with OfficeService
 *
 * Requirements: 1.2, 3.1, 3.2, 3.3, 3.4, 3.5
 */
export function useOfficeForm({
  user,
  office,
  onCreate,
  onUpdate
}: UseOfficeFormOptions): UseOfficeFormReturn {
  const [session, setSession] = useRecoilState(sessionState)
  const navigate = useNavigate()

  // Initialize form data based on whether we're editing or creating
  const getInitialFormData = useCallback(
    (): OfficeFormData => ({
      name: office?.name || '',
      description: office?.description || '',
      address: office?.address || '',
      contactInfo: office?.contactInfo || ''
    }),
    [office]
  )

  const [formData, setFormData] = useState<OfficeFormData>(() =>
    getInitialFormData()
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [successMessage, setSuccessMessage] = useState('')
  const [createdOffice, setCreatedOffice] = useState<Database.Office | null>(
    null
  )
  const [retryCount, setRetryCount] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)

  /**
   * Retry mechanism for network operations
   * Requirements: 5.3 - Network error handling with retry
   */
  const retryOperation = useCallback(
    async (operation: () => Promise<void>, maxRetries = 3): Promise<void> => {
      let lastError: Error | null = null

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          setRetryCount(attempt)
          if (attempt > 0) {
            setIsRetrying(true)
            // Exponential backoff: wait 1s, 2s, 4s between retries
            await new Promise(resolve =>
              setTimeout(resolve, Math.pow(2, attempt - 1) * 1000)
            )
          }

          await operation()
          setIsRetrying(false)
          setRetryCount(0)
          return // Success, exit retry loop
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error))

          // Don't retry validation errors or authentication errors
          if (
            lastError.message.includes('Validation failed:') ||
            lastError.message.includes('authentication') ||
            lastError.message.includes('Authentication')
          ) {
            setIsRetrying(false)
            setRetryCount(0)
            throw lastError
          }

          // Log retry attempt
          ErrorHandler.logWarning(
            `Retry attempt ${attempt + 1}/${maxRetries + 1} failed`,
            { error: lastError.message, attempt },
            { component: 'useOfficeForm', action: 'retryOperation' }
          )
        }
      }

      setIsRetrying(false)
      setRetryCount(0)
      throw lastError || new Error('Operation failed after retries')
    },
    []
  )

  /**
   * Handle authentication errors with redirect
   * Requirements: 8.1 - Authentication error handling with redirects
   */
  const handleAuthenticationError = useCallback(
    (error: Error) => {
      ErrorHandler.logPermissionError(
        'Authentication error during office creation',
        { error: error.message },
        { component: 'useOfficeForm', action: 'handleAuthenticationError' }
      )

      // Clear session and redirect to sign-in
      setSession(old => ({
        ...old,
        user: null,
        isAuthenticating: false
      }))

      navigate('/signin', {
        replace: true,
        state: { message: 'Please sign in to continue creating your office.' }
      })
    },
    [navigate, setSession]
  )

  /**
   * Handle network errors with user-friendly messages
   * Requirements: 5.4 - Network error handling
   */
  const handleNetworkError = useCallback((error: Error): string[] => {
    ErrorHandler.logNetworkError(
      'Network error during office creation',
      { error: error.message },
      { component: 'useOfficeForm', action: 'handleNetworkError' }
    )

    if (
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network request failed') ||
      error.message.includes('offline')
    ) {
      return [
        'Network connection lost. Please check your internet connection and try again.'
      ]
    }

    if (error.message.includes('timeout')) {
      return ['Request timed out. Please try again.']
    }

    return ['Network error occurred. Please try again in a moment.']
  }, [])

  /**
   * Handle database errors with appropriate user feedback
   * Requirements: 8.1 - Database error handling with user notifications
   */
  const handleDatabaseError = useCallback((error: Error): string[] => {
    ErrorHandler.logSystemError(
      'Database error during office creation',
      { error: error.message },
      { component: 'useOfficeForm', action: 'handleDatabaseError' }
    )

    if (
      error.message.includes('permission-denied') ||
      error.message.includes('insufficient permissions')
    ) {
      return [
        'You do not have permission to create offices. Please contact your administrator.'
      ]
    }

    if (error.message.includes('quota-exceeded')) {
      return ['Storage quota exceeded. Please contact support.']
    }

    if (
      error.message.includes('unavailable') ||
      error.message.includes('deadline-exceeded')
    ) {
      return [
        'Service temporarily unavailable. Please try again in a few minutes.'
      ]
    }

    return ['Unable to save office data. Please try again.']
  }, [])

  /**
   * Update a specific form field
   * Requirements: 3.1 - Real-time validation clearing
   */
  const updateField = useCallback(
    (field: keyof OfficeFormData, value: string) => {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
      // Clear validation errors when user starts typing
      if (validationErrors.length > 0) {
        setValidationErrors([])
      }
    },
    [validationErrors.length]
  )

  /**
   * Clear success message
   */
  const clearMessage = useCallback(() => {
    setSuccessMessage('')
  }, [])

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setFormData(getInitialFormData())
    setValidationErrors([])
    setSuccessMessage('')
    setIsSubmitting(false)
    setCreatedOffice(null)
  }, [getInitialFormData])

  /**
   * Submit the form
   * Requirements: 1.2, 3.4, 3.5 - Form submission with OfficeService integration
   */
  const submitForm = useCallback(async () => {
    if (isSubmitting || isRetrying) return

    // Check user authentication - Requirement 8.1
    if (!user?.uid || user.uid === '') {
      setValidationErrors(['User authentication required'])
      return
    }

    setIsSubmitting(true)
    setValidationErrors([])

    try {
      await retryOperation(async () => {
        if (!office) {
          // Create new office
          const newOffice = await OfficeService.createOffice(formData, user)

          // Update session state with new office
          const offices: Database.Office[] = session.offices || []
          const newOfficeList = [...offices, newOffice]
          setSession(old => ({
            ...old,
            offices: newOfficeList
          }))

          setCreatedOffice(newOffice)
          setSuccessMessage('Office created successfully')

          // Reset form after successful creation
          setFormData({
            name: '',
            description: '',
            address: '',
            contactInfo: ''
          })

          onCreate?.(newOffice)
        } else {
          // Update existing office
          if (!office.oid) {
            throw new Error('Office ID is required for update')
          }

          await OfficeService.updateOffice(office.oid, formData, user.uid)

          // Update session state with updated office
          const offices: Database.Office[] = session.offices || []
          const updatedOffices = offices.map(o =>
            o.oid === office.oid
              ? { ...o, ...formData, updatedAt: new Date() }
              : o
          )
          setSession(old => ({
            ...old,
            offices: updatedOffices
          }))

          setSuccessMessage('Office updated successfully')
          onUpdate?.(office.oid)
        }
      })
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      // Handle different types of errors appropriately
      if (
        errorInstance.message.includes('authentication') ||
        errorInstance.message.includes('Authentication')
      ) {
        handleAuthenticationError(errorInstance)
        return
      }

      if (errorInstance.message.includes('Validation failed:')) {
        // Parse validation errors from service
        const validationPart = errorInstance.message.replace(
          'Validation failed: ',
          ''
        )
        const errors = validationPart.split(', ')
        setValidationErrors(errors)
      } else if (
        errorInstance.message.includes('Failed to fetch') ||
        errorInstance.message.includes('Network') ||
        errorInstance.message.includes('timeout')
      ) {
        // Network errors
        const networkErrors = handleNetworkError(errorInstance)
        setValidationErrors(networkErrors)
      } else if (
        errorInstance.message.includes('permission') ||
        errorInstance.message.includes('quota') ||
        errorInstance.message.includes('unavailable')
      ) {
        // Database errors
        const databaseErrors = handleDatabaseError(errorInstance)
        setValidationErrors(databaseErrors)
      } else {
        // Generic error handling
        ErrorHandler.logSystemError(
          'Unexpected error during office creation',
          { error: errorInstance.message },
          { component: 'useOfficeForm', action: 'submitForm' }
        )
        setValidationErrors(['An unexpected error occurred. Please try again.'])
      }
    } finally {
      setIsSubmitting(false)
      setIsRetrying(false)
      setRetryCount(0)
    }
  }, [
    formData,
    isSubmitting,
    isRetrying,
    user,
    office,
    session.offices,
    setSession,
    onCreate,
    onUpdate,
    retryOperation,
    handleAuthenticationError,
    handleNetworkError,
    handleDatabaseError
  ])

  return {
    formData,
    isSubmitting,
    isRetrying,
    retryCount,
    validationErrors,
    successMessage,
    createdOffice,
    updateField,
    submitForm,
    clearMessage,
    resetForm
  }
}

export default useOfficeForm
